import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LocationService } from '@callrail/looky/util';
import { Observable, Subject } from 'rxjs';
import { filter, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';

import { EligibleTrialData } from '../../interfaces/eligible-trial-data';
import { AgencyUsagePricing } from '../../interfaces/usage-pricing';
import { SelectedProducts } from '../../interfaces/product-catalog';
import { StatusData } from '../../interfaces/status-data';
import {
  ZuoraSignatureData,
  ZuoraSignatureRequestReason,
} from '../../interfaces/zuora';
import { BillingSubscriptionModel } from '../../models/billing-subscription.model';
import { createPlatformModel } from '../../models/platform-model.factory';
import { ProductCatalog } from '../../models/product-catalog.model';
import { CurrentContextService } from '../current-context/current-context.service';

@Injectable({
  providedIn: 'root',
})
export class BillingService {
  /**
   * Note that only admins can see subscriptions. When checking an agency's features for any user, instead use agency.features or the hasFeatures function on CurrentContextService
   */
  public billingSubscription$ = this.context.agency.pipe(
    filter((agency) => !!agency),
    switchMap((agency) => this.getSubscription(agency.id)),
    tap(() => this.billingSubscriptionUpdated$.next()),
    shareReplay(1)
  );

  /**
   * Emits an event whenever the billing subscription is updated - useful for timing page redirects
   */
  public billingSubscriptionUpdated$ = new Subject<void>();

  public billingInfo$: Observable<any> = this.context.agency.pipe(
    switchMap((agency) => this.getBillingInfo(agency.id)),
    shareReplay(1)
  );

  public productCatalog$: Observable<ProductCatalog> = this.context.agency.pipe(
    switchMap((agency) => this.getProductCatalog(agency.id)),
    shareReplay(1)
  );

  /**
   * A list of HIPAA plan upgrades the current agency can switch to. Only use this for agencies that are eligible for HIPAA upgrades.
   */
  public hipaaCatalog$: Observable<ProductCatalog> = this.context.agency.pipe(
    switchMap((agency) => this.getHipaaCatalog(agency.id)),
    shareReplay(1)
  );

  public status$: Observable<StatusData> = this.context.agency.pipe(
    switchMap((agency) => this.status(agency.id)),
    shareReplay(1)
  );

  public eligibleTrials$: Observable<EligibleTrialData> =
    this.context.agency.pipe(
      switchMap((agency) => this.trialEligibility(agency.id)),
      shareReplay(1)
    );

  constructor(
    protected http: HttpClient,
    protected locationService: LocationService,
    protected context: CurrentContextService
  ) {}

  public clientToken(): Observable<{ client_token: string }> {
    const url = `/billing/client-token`;
    return this.http.get<{ client_token: string }>(url);
  }

  public getNextInvoice(agencyId: string): Observable<any> {
    const billingUrl = `/a/${agencyId}/billing/next-invoice.json`;
    return this.http.get(billingUrl);
  }

  /**
   * This endpoint returns all of the available platforms and addons that an agency
   * is subscribed to, or was subscribed to if they are not active.
   */
  public getSubscription(
    agencyId: string
  ): Observable<BillingSubscriptionModel> {
    const billingUrl = this.getSubscriptionUrl(agencyId);
    return this.http.get(billingUrl).pipe(
      map((subscriptionData) => {
        return new BillingSubscriptionModel(subscriptionData);
      })
    );
  }

  public updateSubscription(
    agencyId: string,
    subscription: SelectedProducts,
    origin: string = ''
  ): Observable<any> {
    subscription.origin = origin;
    const billingUrl = `/a/${agencyId}/subscription.json`;
    return this.billingSubscription$.pipe(
      take(1),
      switchMap(({ version }) =>
        this.http.patch(billingUrl, { subscription, version })
      )
    );
  }

  public getAllInvoices(agencyId: string, page = 1): Observable<any> {
    const billingUrl = `/a/${agencyId}/billing/all_invoices.json?page=${page}`;
    return this.http.get(billingUrl);
  }

  public createCsv(
    agencyId: string,
    invoiceId: string,
    type: string
  ): Observable<any> {
    const billingUrl = this.createCsvUrl(agencyId, invoiceId, type);
    const params = { agency_id: agencyId, invoice_id: invoiceId };

    return Observable.create((observer) => {
      this.http.post<any>(billingUrl, params).subscribe(
        (resp: { state: string; id: string }) => {
          if (resp.state === 'completed' || resp.state === 'empty') {
            observer.next(resp);
            observer.complete();
          } else {
            this.checkProgress(`${billingUrl}/${resp.id}`, observer, 0);
          }
        },
        (error) => {
          observer.error(error);
          observer.complete();
        }
      );
    });
  }

  public checkProgress(billingUrl: string, observer, pollCount): void {
    this.http.get<any>(billingUrl).subscribe(
      (resp: { state: string }) => {
        if (resp.state === 'completed' || resp.state === 'empty') {
          observer.next(resp);
          observer.complete();
        } else {
          observer.next(resp);
          setTimeout(() => {
            this.checkProgress(billingUrl, observer, pollCount++);
          }, this.getDelay(pollCount));
        }
      },
      (error) => {
        observer.error(error);
        observer.complete();
      }
    );
  }

  public downloadCsv(agencyId: string, invoiceId: string, reportId: string) {
    const billingUrl = `/a/${agencyId}/billing/invoice/${invoiceId}/calls_csv/${reportId}/download`;
    this.locationService.go(billingUrl, true);
  }

  public getSignature(
    reason: ZuoraSignatureRequestReason
  ): Observable<ZuoraSignatureData> {
    const url = '/billing/credit-card-form-params';
    return this.http.get<ZuoraSignatureData>(url, { params: { reason } });
  }

  public reportIframeLoaded(): Observable<void> {
    return this.http.post<void>('/billing/credit-card-form-loaded', {});
  }

  public ccDetails(refId: string): Observable<any> {
    const url = `/billing/credit-card-details.json?payment_method_id=${refId}`;
    return this.http.get(url);
  }

  public saveNewCreditCardPaymentMethod(agencyId, details): Observable<any> {
    const url = `/a/${agencyId}/billing/credit-cards.json`;
    return this.http.post(url, details);
  }

  public bustZuoraAccountCache(agencyId) {
    const url = `/a/${agencyId}/bust_zuora_cache`;
    return this.http.post(url, {});
  }

  public retrial(platforms): Observable<any> {
    // POST so we don't need a stream for the agency ID
    const agencyId = this.context.currentAgency.id;
    const url = `/a/${agencyId}/reactivate_trial`;
    return this.http.post(url, { platforms });
  }

  public agencyUsagePricing(): Observable<AgencyUsagePricing> {
    const agencyId = this.context.currentAgency.id;
    const url = `/a/${agencyId}/subscription/usage_pricing`;
    return this.http.get<AgencyUsagePricing>(url);
  }

  protected getSubscriptionUrl(agencyId: string): string {
    return `/a/${agencyId}/subscription.json`;
  }

  private getDelay(pollCount): number {
    if (pollCount > 20) {
      return 5000;
    } else if (pollCount > 10) {
      return 2000;
    }
    return 1000;
  }

  private createCsvUrl(
    agencyId: string,
    invoiceId: string,
    type: string
  ): string {
    if (type === 'invoice_csv') {
      return `/a/${agencyId}/billing/invoice/${invoiceId}/calls_csv`;
    } else if (type === 'company_csv') {
      return `/a/${agencyId}/billing/invoice/${invoiceId}/calls_csv?type=invoice_company_summary_csv`;
    }
  }

  private getBillingInfo(agencyId: string): Observable<any> {
    const url = `/a/${agencyId}/billing_info.json`;
    return this.http.get(url);
  }

  private getProductCatalog(agencyId: string): Observable<ProductCatalog> {
    const url = `/a/${agencyId}/product_catalog.json`;
    return this.http.get<ProductCatalog>(url).pipe(
      map((productCatalogData) => {
        return new ProductCatalog(productCatalogData);
      })
    );
  }

  private getHipaaCatalog(agencyId: string): Observable<ProductCatalog> {
    const url = `/a/${agencyId}/hipaa_product_catalog.json`;
    return this.http.get<ProductCatalog>(url).pipe(
      map((productCatalogData) => {
        return new ProductCatalog(productCatalogData);
      })
    );
  }

  private status(agencyId): Observable<any> {
    const url = `/a/${agencyId}/status.json`;
    return this.http.get(url);
  }

  private trialEligibility(agencyId: string): Observable<EligibleTrialData> {
    const url = `/a/${agencyId}/subscription/eligible_trials.json`;
    return this.http.get(url).pipe(
      map((data: any) => {
        delete data.chat;
        for (let platform of Object.keys(data)) {
          data[platform].plan = createPlatformModel(
            data[platform].plan,
            platform
          );
        }
        return data;
      })
    );
  }
}
